import { useState } from 'react';
import { useHits } from 'react-instantsearch';

import { useRouter } from '@boss/hooks';
import { IContentItem } from '@boss/services';
import { ISearchObject, OverviewFields } from '@boss/types/b2b-b2c';
import { PageCard, Section } from '@boss/ui';
import { getPagePathByPageType } from '@boss/utils';

import { ComponentMapper, SearchGrid } from '../../components';
import { isB2b } from '../../utils';

type Props = {
  cmsData?: OverviewFields | null;
  indexName: string;
  overviewPageSlug: string;
};

const OverviewPage = ({ cmsData, indexName, overviewPageSlug }: Props) => {
  const [searchValue, setSearchValue] = useState('');
  const { results } = useHits<IContentItem & ISearchObject>();
  const pages = results?.hits;

  const { locale } = useRouter();
  const getDateTimestamp = (page: IContentItem & ISearchObject): number => {
    const dateCandidate = page.publishDate ?? page.lastUpdated;

    if (!dateCandidate || typeof dateCandidate !== 'string') {
      return 0;
    }

    const parsedDate = Date.parse(dateCandidate);

    return Number.isNaN(parsedDate) ? 0 : parsedDate;
  };

  const sortedPages = [...(pages ?? [])].sort((left, right) => {
    const leftHasPriority = typeof left.priority === 'number';
    const rightHasPriority = typeof right.priority === 'number';
    const leftDate = getDateTimestamp(left);
    const rightDate = getDateTimestamp(right);

    if (leftHasPriority && rightHasPriority) {
      if (left.priority && right.priority && left.priority !== right.priority) {
        return left.priority - right.priority;
      }

      if (leftDate !== rightDate) {
        return rightDate - leftDate;
      }

      return left.title.localeCompare(right.title);
    }

    if (!leftHasPriority && !rightHasPriority) {
      if (leftDate !== rightDate) {
        return rightDate - leftDate;
      }

      return left.title.localeCompare(right.title);
    }

    if (!leftHasPriority) {
      if (leftDate !== rightDate) {
        return rightDate - leftDate;
      }

      return 1;
    }

    if (leftDate !== rightDate) {
      return rightDate - leftDate;
    }

    return -1;
  });

  return (
    <div>
      {cmsData?.prefixSections?.map(entry => (
        <ComponentMapper entry={entry} key={entry.id} locale={locale} />
      ))}

      <Section
        className={isB2b ? 'pt-4' : 'pt-10'}
        content={
          <SearchGrid
            attributes={[]}
            compactTopSpacing={isB2b}
            hideTotalResults
            indexName={indexName}
            internalSearch
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            sortOptions={[]}
          >
            {sortedPages.map(page => {
              const image = {
                src: page.mainImage?.src,
                alt: page.title,
              };

              const slug = getPagePathByPageType(page.slug, locale, 'contentPage', page?.tags);

              return (
                <PageCard imageAlt={image?.alt} imageSrc={image?.src} key={page.id} slug={slug} title={page.title} />
              );
            })}
          </SearchGrid>
        }
      />

      {cmsData?.suffixSections?.map(entry => (
        <ComponentMapper entry={entry} key={entry.id} locale={locale} />
      ))}
    </div>
  );
};

export default OverviewPage;
